import { inject, Injectable, NgZone } from '@angular/core';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { defer, from, Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import User, { JwtPayload } from './user';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUser$: Subject<User> = new ReplaySubject<User>(1);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  constructor() {
    this.registerOnAuthEvents();
    this.refresh();
    this.startPeriodicRefresh();
    this.currentUser$.subscribe();
  }

  getCurrentUser(): Observable<User> {
    return this.currentUser$.asObservable();
  }

  getToken(): Observable<string> {
    return defer(() => fetchAuthSession()).pipe(
      map((session) => session.tokens?.idToken?.toString() ?? ''),
      catchError((error) => {
        console.error('Error fetching auth session:', error);
        return of('');
      }),
    );
  }

  async logout(): Promise<void> {
    try {
      await signOut();
      this.reset();
      await this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  }

  private registerOnAuthEvents(): void {
    Hub.listen('auth', (data) => {
      this.zone.run(() => {
        const { event } = data.payload;
        switch (event) {
          case 'signedIn':
          case 'tokenRefresh':
            this.refresh();
            break;
          case 'tokenRefresh_failure':
          case 'signedOut':
            this.reset();
            this.router.navigate(['/login']);
            break;
        }
      });
    });
  }

  private startPeriodicRefresh(): void {
    setInterval(
      () => {
        this.refresh();
      },
      10 * 60 * 1000,
    );
  }

  refreshForced(): Observable<void> {
    return from(fetchAuthSession({ forceRefresh: true })).pipe(
      tap((session) => {
        const token = session.tokens?.idToken;
        if (token) {
          const user = User.fromDto(token.payload as unknown as JwtPayload);
          this.currentUser$.next(user);
        } else {
          this.reset();
        }
      }),
      map(() => undefined),
      catchError((error) => {
        this.reset();
        return throwError(() => error);
      }),
    );
  }

  private refresh(): void {
    from(fetchAuthSession())
      .pipe(
        map((session) => session.tokens?.idToken),
        tap((token) => {
          if (token) {
            const user = User.fromDto(token.payload as unknown as JwtPayload);
            this.currentUser$.next(user);

            if (user.isKind() && this.router.url !== '/self-checkin') {
              this.router.navigate(['/self-checkin']);
            } else if (user.isVolunteerTablet() && this.router.url !== '/self-checkin-volunteer') {
              this.router.navigate(['/self-checkin-volunteer']);
            }
          } else {
            this.reset();
          }
        }),
        catchError((e) => {
          console.error('Forced refresh failed: ', e);
          this.reset();
          return of(null);
        }),
      )
      .subscribe();
  }

  private reset(): void {
    this.currentUser$.next(User.anonymousUser());
  }
}
